import { VariableSelector } from '@atoms/dataSelectors/variableSelector/variableSelector';
import { InputField } from '@atoms/input';
import BlockBase from '@components/dnd/base/blockBase';
import { Column } from '@components/dnd/base/blockBase/body.block.style';
import { useAtom } from '@hooks/useAtom';
import EndControlsFB from '@library/formBuilder/endControls';
import {
  Checkbox,
  FormControl,
  FormLabel,
  ListItemDecorator,
  Option,
  Radio,
  RadioGroup,
  Select,
  type SelectOption
} from '@mui/joy';
import {
  type DNDBlock,
  TO_NUMBER_VIEW_TYPES,
  TO_NumberIconSchema
} from '@stratumn/studio-shared';
import { Hash, type LucideIcon } from 'lucide-react';
import { observer } from 'mobx-react';
import type { ChangeEvent } from 'react';
import { useParams } from 'react-router-dom';
import { ParamsList } from '@/routes/routes.types';
import { newError } from '@/services/errors/errors';
import { isValidType } from '@/utils/parseZodSchema';

import { LabeledVariable } from '../blocks.style';
import { initialTONumberData } from './number.data';
import { numberIconOptions } from './number.utils';

export const NumberIcon: LucideIcon = Hash;

const renderSelectedIconOption = (option: SelectOption<string> | null) => {
  if (!option) {
    return null;
  }

  if (!isValidType(TO_NumberIconSchema, option.value, 'TO_Icon-klqjw')) {
    throw newError('TO_Icon-klqjw', `Invalid icon option "${option.value}"`);
  }

  const icon = numberIconOptions.find(
    iconOption => iconOption.value === option.value
  )?.icon;

  return (
    <>
      <ListItemDecorator>{icon}</ListItemDecorator>
      {option.label}
    </>
  );
};

const Number = (block: DNDBlock) => {
  const workflowId = useParams()[ParamsList.WorkflowId] as string;

  const atom = useAtom({
    type: 'TO_Number',
    sourceId: block.id,
    parentId: workflowId,
    parentKind: 'traceOverview',
    initialData: {
      ...initialTONumberData,
      /* eslint-disable */
      selectedVariable: block.other.selectedVariableRef!
    }
  });

  if (!atom) {
    return null;
  }

  const onTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
    atom.data.title = event.target.value;
  };

  return (
    <BlockBase
      dataItem={atom}
      dndBlock={block}
      endControls={
        <EndControlsFB dataItem={atom} dndBlock={block} hasRequired={false} />
      }
      icon={NumberIcon}
      title={atom.data.title}
    >
      <Column>
        <InputField
          label="Widget title"
          onChange={onTitleChange}
          placeholder="Aa"
          value={atom.data.title}
        />
      </Column>
      <Column $width="200px">
        <FormControl size="sm">
          <FormLabel> State variable</FormLabel>
          <VariableSelector
            disabled
            editable={false}
            selectedRef={atom.data.selectedVariable}
          />
        </FormControl>
      </Column>
      <Column>
        <FormControl size="sm">
          <FormLabel>Display type</FormLabel>
          <RadioGroup name="radio-buttons-group" value={atom.data.viewType}>
            {TO_NUMBER_VIEW_TYPES.map(viewType => (
              <Radio
                color="neutral"
                key={viewType}
                label={viewType}
                onChange={e =>
                  (atom.data.viewType = e.target.value as typeof viewType)
                }
                size="sm"
                value={viewType}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Column>
      <Column>
        <LabeledVariable>
          <FormLabel>Icon</FormLabel>
          <Checkbox
            checked={atom.data.hasIcon}
            color="neutral"
            label="Add an icon"
            onChange={e => (atom.data.hasIcon = e.target.checked)}
            size="sm"
            variant="outlined"
          />
          <Select
            disabled={!atom.data.hasIcon}
            onChange={(_, newValue) => {
              if (newValue) {
                atom.data.icon = newValue;
              }
            }}
            placeholder="Select an icon…"
            renderValue={renderSelectedIconOption}
            size="sm"
            sx={{
              '--ListItemDecorator-size': '20px',
              marginTop: '10px',
              width: '100%'
            }}
            value={atom.data.icon}
          >
            {numberIconOptions.map(iconOption => (
              <Option key={iconOption.value} value={iconOption.value}>
                <ListItemDecorator>{iconOption.icon}</ListItemDecorator>
                {iconOption.label}
              </Option>
            ))}
          </Select>
        </LabeledVariable>
      </Column>
    </BlockBase>
  );
};

export default observer(Number);
